import { createRoot } from 'react-dom/client';

import { LOCALE } from '@/lib/constants/i18n';
import Widget from './components/Widget';
import Banner from './components/Banner';
import { SESSION_ID_KEY } from './constants';

export default class TradeIn extends HTMLElement {
  private shadow = this.attachShadow({ mode: 'closed' });
  private initialized = false;

  static observedAttributes = [
    'data-client-id',
    'data-trade-in-id',
    'data-sku',
    'data-campaign',
    'data-small',
    'data-partner-id',
    'data-order-id',
    'data-locale',
  ];

  constructor() {
    super();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (!this.initialized) return;
    console.log(
      `Attribute ${name} has changed from ${oldValue} to ${newValue}.`,
    );
    this.renderWidget();
  }

  connectedCallback() {
    this.renderWidget();
  }

  getStyles(): string | null {
    return null;
  }

  getCustomBanner(): typeof Banner | null {
    return null;
  }

  updateStylesheet() {
    const styles = this.getStyles();

    if (styles) {
      /* 
        This fixes an issue with tailwind v4 producting non shadow root compliant @property rules
        @property rules are instead added to the global stylesheet
        https://github.com/tailwindlabs/tailwindcss/issues/15005#issuecomment-2737489813
      */
      const shadowSheet = new CSSStyleSheet();
      shadowSheet.replaceSync(
        styles
          // Since these will end up in the global stylesheet, we need to change prefixes
          .replace(/--tw/g, '--onlineti'),
      );
      const globalSheet = new CSSStyleSheet();
      for (const rule of shadowSheet.cssRules) {
        if (
          rule instanceof CSSPropertyRule ||
          rule instanceof CSSFontFaceRule
        ) {
          globalSheet.insertRule(rule.cssText);
        }
      }

      document.adoptedStyleSheets.push(globalSheet);
      this.shadow.adoptedStyleSheets = [shadowSheet];
    }
  }

  async renderWidget() {
    this.initialized = true;

    const sku = this.getAttribute('data-sku');
    const ean = this.getAttribute('data-ean');
    const small = this.hasAttribute('data-small');
    const clientId = this.getAttribute('data-client-id');
    const orderId = this.getAttribute('data-order-id');
    const partnerId = this.getAttribute('data-partner-id');
    const locale = this.getAttribute('data-locale') as LOCALE | null; // Not required

    const purchaseUrl = window.location.href;

    // Trade-in ID can be passed in three ways:
    // 1. As a URL hash parameter
    // 2. As a data attribute
    // 3. As a session storage item
    const hashTradeInId = new URLSearchParams(
      window.location.hash.replace('#', ''),
    ).get('tradeInId');
    const dataTradeInId = this.getAttribute('data-trade-in-id');
    const sessionTradeInId = sessionStorage.getItem(SESSION_ID_KEY);
    const tradeInId =
      hashTradeInId || dataTradeInId || sessionTradeInId || null;

    // Campaign code can be passed in four ways:
    // 1. As a data attribute
    // 2. As a URL search parameter
    // 3. As a URL hash parameter
    // 4. As a session storage item
    const dataCampaignCode = this.getAttribute('data-campaign');
    const searchCampaignCode = new URLSearchParams(
      window.location.search.replace('?', ''),
    ).get('utm_campaign');
    const hashCampaignCode = new URLSearchParams(
      window.location.hash.replace('#', ''),
    ).get('utm_campaign');
    const storageCampaignCode = sessionStorage
      ?.getItem('tracking')
      ?.split('utm_campaign=')?.[1];

    const campaignCode =
      dataCampaignCode ||
      searchCampaignCode ||
      hashCampaignCode ||
      storageCampaignCode ||
      null;

    const CustomBanner = this.getCustomBanner();

    if (!partnerId || (!ean && !sku && !tradeInId)) return null;

    this.updateStylesheet();

    const root = createRoot(this.shadow);
    root.render(
      <Widget
        partnerId={partnerId}
        clientId={clientId}
        tradeInId={tradeInId}
        orderId={orderId}
        sku={sku}
        ean={ean}
        purchaseUrl={purchaseUrl}
        campaignCode={campaignCode}
        small={small}
        locale={locale}
        CustomBanner={CustomBanner}
      />,
    );
  }
}
